import React from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bot, CheckCircle, XCircle, AlertTriangle, AlertOctagon, Sparkles } from 'lucide-react';

export default function AITaskApprovalSection({ activity, pendingTasks, isAccountable }) {
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = React.useState(false);

  const generateTasksMutation = useMutation({
    mutationFn: async () => {
      setIsGenerating(true);
      const response = await base44.functions.invoke('generateTasks', {
        activity_id: activity.id,
        mode: 'generate',
        target: 'bot'
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
      setIsGenerating(false);
    },
    onError: (error) => {
      console.error('Failed to generate tasks:', error);
      alert('Failed to generate tasks. Please try again.');
      setIsGenerating(false);
    }
  });

  const approveTasksMutation = useMutation({
    mutationFn: async () => {
      const response = await base44.functions.invoke('generateTasks', {
        activity_id: activity.id,
        mode: 'approve'
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
    }
  });

  const rejectTasksMutation = useMutation({
    mutationFn: async () => {
      const response = await base44.functions.invoke('generateTasks', {
        activity_id: activity.id,
        mode: 'reject'
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
    }
  });

  if (!isAccountable) return null;

  return (
    <div className="space-y-3">
      {pendingTasks.length > 0 ? (
        <>
          <div className="flex items-center gap-2 mb-2">
            <Bot className="w-4 h-4 text-indigo-400" />
            <span className="text-xs text-slate-400 font-semibold uppercase">AI Generated Tasks Pending Approval</span>
            <Badge className="bg-amber-500/20 text-amber-400 border-none">
              {pendingTasks.length} pending
            </Badge>
          </div>
          
          {pendingTasks.map((task) => (
            <Card key={task.id} className="bg-slate-900/50 border-slate-700">
              <CardContent className="p-3">
                <div className="flex items-start gap-2 mb-2">
                  <Bot className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-slate-300 flex-1">{task.task_description}</p>
                </div>
                
                {task.key_result_focus && (
                  <div className="text-xs text-slate-500 mb-2">
                    KR Focus: {task.key_result_focus}
                  </div>
                )}
                
                {task.ai_assessment && task.ai_assessment.type !== 'ok' && (
                  <div className={`text-xs p-2 rounded-md flex items-start gap-2 mb-2 ${
                    task.ai_assessment.type === 'redundant' 
                      ? 'bg-red-500/10 border border-red-500/30 text-red-200'
                      : 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-200'
                  }`}>
                    {task.ai_assessment.type === 'redundant' ? (
                      <AlertOctagon className="w-3 h-3 mt-0.5 shrink-0" />
                    ) : (
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    )}
                    <span>{task.ai_assessment.message}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => approveTasksMutation.mutate()}
              disabled={approveTasksMutation.isLoading}
              className="bg-emerald-600 hover:bg-emerald-700 flex-1">
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve All ({pendingTasks.length})
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => rejectTasksMutation.mutate()}
              disabled={rejectTasksMutation.isLoading}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 flex-1">
              <XCircle className="w-4 h-4 mr-2" />
              Reject All
            </Button>
          </div>
        </>
      ) : (
        <Button
          size="sm"
          onClick={() => generateTasksMutation.mutate()}
          disabled={isGenerating}
          className="w-full bg-indigo-600 hover:bg-indigo-700">
          <Sparkles className="w-4 h-4 mr-2" />
          {isGenerating ? 'Generating Tasks...' : 'Generate AI Tasks'}
        </Button>
      )}
    </div>
  );
}